import { usePrivacyPolicyOpener } from "@/client-mobile-app/hooks/usePrivacyPolicyOpener";
import { useTermsOfServiceOpener } from "@/client-mobile-app/hooks/useTermsOfServiceOpener";
import { useLogout } from "@/common/hooks/use-logout";
import { useTRPC } from "@/common/utils/trpc";
import { useGetCurrentUserQuery } from "@/setup/frontend/api/apiHooks";
import { IfImpersonating } from "@/users/components/IfImpersonating";
import { TOTPStatus } from "@/auth/components/TOTPStatus";
import { PasskeyManagement } from "@/auth/components/PasskeyManagement";
import { FeatureFlag } from "@/feature-flags/featureFlags";
import { useFeatureFlag } from "@/feature-flags/useFeatureFlag";
import { StopImpersonatingUserButton } from "@/users/operations/stop-impersonating-user/StopImpersonatingUserButton";
import {
	Drawer,
	Group,
	Divider,
	Stack,
	Button,
	Text,
	Image,
	Title,
	useMantineColorScheme,
	useComputedColorScheme,
	Modal,
} from "@mantine/core";
import {
	IconClipboardText,
	IconFileText,
	IconFingerprint,
	IconLogout,
	IconMasksTheaterOff,
	IconShieldCheck,
	IconSun,
	IconSettings,
} from "@tabler/icons-react";
import { IconMoon } from "@tabler/icons-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { IvrInstructionsButton } from "../../components/IvrInstructionsButton";

export function CaregiverAppSettingsDrawer({
	opened,
	onClose,
}: {
	opened: boolean;
	onClose: () => void;
}) {
	const queryClient = useQueryClient();
	const user = useGetCurrentUserQuery();
	const { setColorScheme } = useMantineColorScheme();
	const computedColorScheme = useComputedColorScheme("light", {
		getInitialValueInEffect: true,
	});

	const [
		setupAuthenticatorAppModalOpened,
		setSetupAuthenticatorAppModalOpened,
	] = useState(false);
	const [passkeyModalOpened, setPasskeyModalOpened] = useState(false);
	const canManagePasskeys = useFeatureFlag(FeatureFlag.PasskeyManagement);
	const onPrivacyPolicyClicked = usePrivacyPolicyOpener();
	const onTermsOfServiceClicked = useTermsOfServiceOpener();

	const logout = useLogout();

	const trpc = useTRPC();
	const stopImpersonatingUserMutation = useMutation(
		trpc.users.stopImpersonatingUser.mutationOptions({
			onSuccess: () => {
				window.location.reload();
			},
		}),
	);

	return (
		<Drawer.Root
			opened={opened}
			onClose={() => {
				setSetupAuthenticatorAppModalOpened(false);
				setPasskeyModalOpened(false);
				onClose();
			}}
			position="left"
			size="sm"
			h="100%"
		>
			<Drawer.Overlay />

			<Drawer.Content
				h="100%"
				w="80%"
				style={{
					display: "flex",
					flexDirection: "column",
					flex: 1,
				}}
			>
				<Drawer.Header
					style={{
						// place top padding on header to avoid scroll content from being visible above the header
						paddingTop:
							"calc(var(--safe-area-inset-top) + var(--mantine-spacing-xs))",
					}}
				>
					<Group justify="space-between" align="center" w="100%">
						<Image src="/logo512.png" w={30} />
						<Drawer.Title>
							{[user.data?.firstName, user.data?.lastName]
								.filter(Boolean)
								.join(" ")}

							<Text c="dimmed" size="xs">
								Settings
							</Text>
						</Drawer.Title>
						<Drawer.CloseButton />
					</Group>
				</Drawer.Header>
				<Divider />

				<Drawer.Body
					style={{ display: "flex", flexDirection: "column", flex: 1 }}
				>
					<Stack mt="md">
						<Button
							component={Link}
							to="/app/care-sheets"
							variant="default"
							size="xs"
							justify="flex-start"
							fullWidth
							leftSection={<IconFileText size={16} />}
						>
							Care Sheets
						</Button>
						<Button
							component={Link}
							to="/app/forms"
							variant="default"
							size="xs"
							justify="flex-start"
							fullWidth
							leftSection={<IconClipboardText size={16} />}
						>
							Forms
						</Button>
						<Button
							onClick={() =>
								setColorScheme(
									computedColorScheme === "light" ? "dark" : "light",
								)
							}
							variant="default"
							size="xs"
							justify="flex-start"
							fullWidth
							leftSection={
								computedColorScheme === "light" ? (
									<IconMoon size={16} />
								) : (
									<IconSun size={16} />
								)
							}
						>
							{computedColorScheme === "light" ? "Dark Mode" : "Light Mode"}
						</Button>

						{import.meta.env.VITE_CAPACITOR_BUILD === "true" && (
							<Button
								component={Link}
								to="/app/onboarding"
								variant="default"
								size="xs"
								justify="flex-start"
								fullWidth
								leftSection={<IconSettings size={16} />}
							>
								Onboarding Setup
							</Button>
						)}
						{import.meta.env.VITE_CAPACITOR_BUILD && (
							<Button
								variant="default"
								size="xs"
								onClick={onPrivacyPolicyClicked}
								justify="flex-start"
								fullWidth
							>
								Privacy Policy
							</Button>
						)}
						{import.meta.env.VITE_CAPACITOR_BUILD && (
							<Button
								variant="default"
								size="xs"
								onClick={onTermsOfServiceClicked}
								justify="flex-start"
								fullWidth
							>
								Terms of Service
							</Button>
						)}
						<IvrInstructionsButton justify="flex-start" fullWidth />
						<Button
							variant="light"
							color="blue"
							size="xs"
							onClick={() => setSetupAuthenticatorAppModalOpened(true)}
							justify="flex-start"
							fullWidth
							leftSection={<IconShieldCheck size={16} />}
						>
							Authenticator App
						</Button>
						{canManagePasskeys && (
							<Button
								variant="light"
								color="blue"
								size="xs"
								onClick={() => setPasskeyModalOpened(true)}
								justify="flex-start"
								fullWidth
								leftSection={<IconFingerprint size={16} />}
							>
								Passkeys
							</Button>
						)}
						<IfImpersonating>
							<Button
								variant="light"
								color="blue"
								size="xs"
								onClick={() => stopImpersonatingUserMutation.mutate({})}
								justify="flex-start"
								fullWidth
								leftSection={<IconMasksTheaterOff size={16} />}
							>
								Stop Impersonating
							</Button>
						</IfImpersonating>
					</Stack>
					<Stack
						style={{ flex: 1 }}
						gap="xs"
						align="flex-start"
						justify="flex-end"
					>
						<Button
							variant="light"
							color="yellow"
							size="xs"
							onClick={() => {
								logout.mutate();
							}}
							justify="flex-start"
							fullWidth
							leftSection={<IconLogout size={16} />}
							loading={logout.isPending}
						>
							Logout
						</Button>
						<Group justify="space-between" align="center" w="100%">
							<Group>
								<Text c="dimmed">v{import.meta.env.VITE_APP_VERSION}</Text>
							</Group>
							<IfImpersonating>
								<StopImpersonatingUserButton />
							</IfImpersonating>
						</Group>
					</Stack>

					<Modal
						title="Authenticator App"
						size="xl"
						opened={setupAuthenticatorAppModalOpened}
						onClose={() => setSetupAuthenticatorAppModalOpened(false)}
						styles={{
							content: {
								paddingBottom: "var(--safe-area-inset-bottom)",
								paddingLeft: "var(--safe-area-inset-left)",
								paddingRight: "var(--safe-area-inset-right)",
								backgroundColor: "var(--mantine-color-body)",
							},
						}}
						withCloseButton
						centered
					>
						<TOTPStatus
							setupAuthenticatorAppModalSize="xl"
							onRefresh={() => {
								queryClient.invalidateQueries({
									queryKey: ["userMFAStatus"],
								});
								setSetupAuthenticatorAppModalOpened(false);
							}}
						/>
					</Modal>

					{canManagePasskeys && (
						<Modal
							title="Passkeys"
							opened={passkeyModalOpened}
							onClose={() => setPasskeyModalOpened(false)}
							styles={{
								content: {
									paddingBottom: "var(--safe-area-inset-bottom)",
									paddingLeft: "var(--safe-area-inset-left)",
									paddingRight: "var(--safe-area-inset-right)",
									backgroundColor: "var(--mantine-color-body)",
								},
							}}
							withCloseButton
							centered
						>
							<PasskeyManagement />
						</Modal>
					)}
				</Drawer.Body>
			</Drawer.Content>
		</Drawer.Root>
	);
}
